/**
 * OfflineBanner and SyncStatusPill — the connectivity surface.
 *
 * Used by: the Admin app shell (a manager on hotel wifi needs to know the
 * numbers on screen are stale), and — far more importantly — the POS and Staff
 * shells, where an offline device keeps taking orders into the outbox and the
 * cashier MUST be able to see that at a glance.
 *
 * The rule these two encode: never let a user believe a write reached the
 * server when it is still sitting in a queue on the device.
 *
 * Accessibility: the banner is `role="status"` + `aria-live="polite"` so the
 * transition to offline is announced once, and the pill exposes its state as
 * text, not only as a colour.
 */
import type { ReactNode } from 'react';
import { cn } from '../utils.js';
import { TONE_CLASSES, type Tone } from '../status.js';

export interface OfflineBannerProps {
  readonly online: boolean;
  /** Number of mutations waiting in the offline outbox, if the app has one. */
  readonly pendingCount?: number;
  readonly onRetry?: () => void;
  readonly className?: string;
}

export function OfflineBanner({
  online,
  pendingCount = 0,
  onRetry,
  className,
}: OfflineBannerProps): React.JSX.Element | null {
  if (online && pendingCount === 0) return null;

  const tone: Tone = online ? 'warning' : 'danger';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-wrap items-center justify-center gap-2 px-4 py-2 text-sm font-medium',
        TONE_CLASSES[tone].soft,
        className,
      )}
    >
      <span aria-hidden="true">{online ? '⟳' : '⚡'}</span>
      {online ? (
        <span>
          Đã có mạng trở lại. Đang đồng bộ <strong className="sa-tabular">{pendingCount}</strong> thao tác chờ gửi.
        </span>
      ) : (
        <span>
          Đang ngoại tuyến. Dữ liệu hiển thị có thể đã cũ
          {pendingCount > 0 ? (
            <>
              {' '}
              và <strong className="sa-tabular">{pendingCount}</strong> thao tác đang chờ gửi
            </>
          ) : null}
          .
        </span>
      )}
      {onRetry !== undefined ? (
        <button type="button" onClick={onRetry} className="underline underline-offset-4">
          Thử lại ngay
        </button>
      ) : null}
    </div>
  );
}

export type SyncState = 'synced' | 'syncing' | 'pending' | 'offline' | 'error';

const SYNC_TONE: Record<SyncState, Tone> = {
  synced: 'success',
  syncing: 'info',
  pending: 'warning',
  offline: 'danger',
  error: 'danger',
};

const SYNC_LABEL: Record<SyncState, string> = {
  synced: 'Đã đồng bộ',
  syncing: 'Đang đồng bộ',
  pending: 'Chờ đồng bộ',
  offline: 'Ngoại tuyến',
  error: 'Lỗi đồng bộ',
};

export interface SyncStatusPillProps {
  readonly state: SyncState;
  /** Pending mutation count; rendered inside the pill when > 0. */
  readonly pendingCount?: number;
  /** Epoch ms of the last successful server round trip. */
  readonly lastSyncedAt?: number | null;
  readonly onClick?: () => void;
  readonly className?: string;
}

export function SyncStatusPill({
  state,
  pendingCount = 0,
  lastSyncedAt,
  onClick,
  className,
}: SyncStatusPillProps): React.JSX.Element {
  const tone = SYNC_TONE[state];
  const label = SYNC_LABEL[state];
  const title =
    lastSyncedAt !== undefined && lastSyncedAt !== null
      ? `Lần đồng bộ gần nhất: ${new Date(lastSyncedAt).toLocaleTimeString('vi-VN')}`
      : label;

  const content: ReactNode = (
    <>
      <span
        aria-hidden="true"
        className={cn('h-2 w-2 rounded-full', TONE_CLASSES[tone].dot, state === 'syncing' && 'animate-pulse')}
      />
      <span>{label}</span>
      {pendingCount > 0 ? <span className="sa-tabular font-semibold">· {pendingCount}</span> : null}
    </>
  );

  const classes = cn(
    'inline-flex min-h-touch items-center gap-2 rounded-full px-3 text-xs font-medium',
    TONE_CLASSES[tone].soft,
    className,
  );

  if (onClick === undefined) {
    return (
      <span className={classes} title={title} aria-label={title}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(classes, 'outline-none focus-visible:ring-2 focus-visible:ring-ring')}
    >
      {content}
    </button>
  );
}
